'use client';

import { isClient } from '@/utils/is-client';
import {
	empty,
	Subject,
	fromEvent,
	ReplaySubject,
	type Observable,
} from 'rxjs';
import {
	tap,
	take,
	skip,
	share,
	buffer,
	filter,
	mergeAll,
	skipUntil,
} from 'rxjs/operators';

import * as EventTypes from './types';

export * from './types';
export type { Observable } from 'rxjs';
export { tap, map, buffer, filter, bufferTime } from 'rxjs';

export interface ArenaEvent {
	type: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	value?: any;
}
export type Event = ArenaEvent;

export type EventBus = Subject<ArenaEvent>;
export const events$: EventBus = new Subject<ArenaEvent>().pipe(
	filter(({ type }) => type !== EventTypes.ARENA_APP_EVENT), // Prevent infinite loop
	tap(
		({ type, value: detail }) =>
			isClient &&
			window.dispatchEvent(new window.CustomEvent(type, { detail })),
	),
	share(),
) as EventBus;
const eventsDebug$ = new ReplaySubject<ArenaEvent>(100);
const debugEventsSub = events$.subscribe(eventsDebug$);
eventsDebug$.next({ type: 'events.init' });

export const eventsFilterByType = (eventType: string) =>
	events$.pipe(filter((e) => e.type === eventType));

export const eventsStartsWith = (eventPrefix: string) =>
	events$.pipe(filter((e) => e.type.startsWith(eventPrefix)));

export const windowError$ = isClient ? fromEvent(window, 'error') : empty();
export const windowUnhandledRejections$ = isClient
	? fromEvent(window, 'unhandledrejection')
	: empty();

// buffer `src$` until `done$` emits, then emit buffered events and let `src$` emit freely
export const bufferUntil = <DoneEvent = unknown, SrcEvent = unknown>({
	done$,
	src$,
}: {
	done$: Observable<DoneEvent>;
	src$: Observable<SrcEvent>;
}) => {
	const srcShared$ = src$.pipe(share());
	return [
		srcShared$.pipe(buffer(done$), mergeAll()),
		srcShared$.pipe(skipUntil(done$)),
	];
};

/* eslint-disable prettier/prettier, no-console */
if (isClient) {
	window.addEventListener(
		EventTypes.ARENA_APP_EVENT,
		(
			{ detail }: any, // eslint-disable-line @typescript-eslint/no-explicit-any
		) => events$.next(detail),
	);

	const searchParams = new URLSearchParams(document.location.search);

	if (searchParams.has('rvnEventsDebug')) {
		const rvnPerformanceDebugSub = eventsDebug$.subscribe(({ type, value: detail }) => {
			const name = `rvnMark:${type}`;
			const mark = window.performance.mark(name);
			console.debug(
				`rvnEvt:${type}`,
				Math.trunc(mark.startTime),
				detail,
			);
		});
	}

	if (searchParams.has('rvnEventsDebugStart') && searchParams.has('rvnEventsDebugEnd')) {
		const startParam = searchParams.get('rvnEventsDebugStart');
		const endParam = searchParams.get('rvnEventsDebugEnd');
		const rvnPerformanceMeasureSub = eventsDebug$
			.pipe(
				filter(({ type }) => type === startParam || type === endParam),
			)
			.subscribe(({ type, value: detail }) => {
				const startMark = `rvnMark:${searchParams.get('rvnEventsDebugStart')}`;
				const endMark = `rvnMark:${searchParams.get('rvnEventsDebugEnd')}`;
				if (`rvnMark:${type}` === startMark) {
					const mark = window.performance.mark(startMark);
					console.debug(
						startMark,
						Math.trunc(mark.startTime),
					);
				} else if (`rvnMark:${type}` === endMark) {
					const measureName = `${startMark}-${endMark}`;
					const mark = window.performance.mark(endMark);
					const measure = window.performance.measure(measureName, startMark, endMark);
					console.debug(
						measureName,
						' duration: ', Math.trunc(measure.duration),
						' startTimeTSPL: ', Math.trunc(measure.startTime),
						' endTimeTSPL: ', Math.trunc(mark.startTime),
					);
				}
			});
	}
	window.rvnEvents = eventsDebug$;
	window.rvnEventsDebug = () => eventsDebug$.subscribe((...args) => console.debug('rvnEvt', ...args));
	window.addEventListener('hashchange', () => {
		window.location.hash.includes('rvnEventsDebug');
		const debugSub = window.rvnEventsDebug();
	});

	const removeSEOSchemaSub = eventsFilterByType(EventTypes.ANALYTICS_PAGE_VIEW)
		.pipe(skip(1), take(1))
		.subscribe((e) => {
			const seoScript = document.getElementById('seo-schema');
			if (seoScript) {
				seoScript.remove();
			}
		});
}
/* eslint-enable */
